import { useEffect, useState } from "react";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";
import { LiveKitRoom } from "@livekit/components-react";
import "@livekit/components-styles";

import type { MeetingAccess } from "../../domain/meetingAccess";
import {
    meetingJoinFailureFrom,
    MEETING_ROOM_OPTIONS,
    requestGuestMeetingToken,
    sessionForAppointment,
} from "../../application/meetings";
import { useTherapist } from "../../../../context/useTherapist";
import MeetingStage from "../components/MeetingStage";
import { MeetingHeader, MeetingRoom } from "../components/MeetingChrome";
import { MeetingElapsed } from "../components/MeetingTimer";
import MeetingLobby from "../components/MeetingLobby";

/**
 * The room a join link opens, read from the link itself.
 *
 * The room name is the first half of the token and is not secret — the
 * signature is what makes the link worth anything — so a therapist's own
 * browser can tell which room a link leads to without asking the API.
 */
function roomFromToken(token: string | undefined): string | null {
    if (!token || !token.includes(".")) return null;
    const [encoded] = token.split(".");
    try {
        return atob(encoded.replace(/-/g, "+").replace(/_/g, "/"));
    } catch {
        return null;
    }
}

/**
 * What a refusal to hand over a join token actually means, in the reader's
 * words.
 *
 * Four answers, and they are not interchangeable. A link we never issued is
 * not found. A link we did issue can still lead to a room that does not open
 * right now: early is a wait, late is an ending, and a session called off is
 * neither. Telling someone their session "is not valid" when it simply has
 * not started yet sends them to their therapist over an hour. Anything else
 * is ours to apologise for.
 */
function refusal(err: unknown, t: TFunction): string {
    const failure = meetingJoinFailureFrom(err);

    if (failure.kind === "notFound") return t("guestMeeting.notFound");
    if (failure.kind !== "refused") return t("guestMeeting.unavailable");

    const { reason, opensAt } = failure.refusal;

    if (reason === "cancelled") return t("guestMeeting.cancelled");

    // The server's own verdict, not a comparison against this device's
    // clock: a phone an hour fast would otherwise read "come back later"
    // as "it is over".
    if (reason === "notOpenYet" && opensAt) {
        return t("guestMeeting.notOpenYet", {
            when: opensAt.toLocaleString(undefined, {
                dateStyle: "long",
                timeStyle: "short",
            }),
        });
    }
    return t("guestMeeting.closed");
}

/**
 * Attending a session from the link, with no account of any kind.
 *
 * This is how a patient joins: the same bargain Zoom and Meet make. The link
 * is the authorisation — signed by us and naming one room — so there is
 * nothing to sign in to and nothing to install.
 *
 * The name is asked for rather than assumed. Nobody is identified here, so the
 * alternative is a room of anonymous rectangles, and a therapist who cannot
 * tell which one is their patient.
 *
 * Every root here is `translate="no"`: a patient joins from whatever phone
 * they have, and a browser translating the page rewrites the DOM underneath
 * React — which crashed this page for real patients mid join. The copy is
 * already in the patient's language, so there is nothing to lose by opting
 * out and a session to lose by not.
 */
export default function GuestMeetingPage() {
    const { t } = useTranslation();
    const { token } = useParams<{ token: string }>();
    const navigate = useNavigate();
    const { therapist } = useTherapist();

    const [name, setName] = useState("");
    const [access, setAccess] = useState<MeetingAccess | null>(null);
    const [joining, setJoining] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [ended, setEnded] = useState(false);
    const [connected, setConnected] = useState(false);
    /**
     * What the patient chose in the lobby, carried into the room.
     *
     * Both default to on — the expected way to attend a session is with your
     * camera and microphone working — but the choice exists and is made before
     * anyone can see or hear them.
     */
    const [devices, setDevices] = useState({ microphone: true, camera: true });

    /**
     * A therapist following their own link belongs in their own room.
     *
     * The link in a booking or a confirmation email is the patient's, and the
     * therapist reaches for the same one — but joining as a guest would cost
     * them the session dock, the recording and the finish button, and would
     * put them in the room under a typed-in name.
     */
    useEffect(() => {
        if (!therapist?.id) return;
        const room = roomFromToken(token);

        if (room?.startsWith("sess-")) {
            navigate(`/meet/session/${room.slice("sess-".length)}`, {
                replace: true,
            });
            return;
        }

        if (!room?.startsWith("appt-")) return;

        // A booking has no session until the therapist turns up, so ask for
        // one — the same booking always answers with the same session, so
        // reopening the link does not start a second.
        const appointmentId = room.slice("appt-".length);
        let cancelled = false;
        sessionForAppointment(appointmentId)
            .then((appointmentSession) => {
                if (!cancelled)
                    navigate(`/meet/session/${appointmentSession.session_id}`, {
                        replace: true,
                    });
            })
            .catch(() => {
                // Too early for the room, or the session could not be made.
                // The plain room still gets them into the call.
                if (!cancelled) navigate(`/meet/${appointmentId}`, { replace: true });
            });

        return () => {
            cancelled = true;
        };
    }, [therapist?.id, token, navigate]);

    const join = async () => {
        if (!token || joining) return;
        setJoining(true);
        setError(null);
        try {
            const meetingAccess = await requestGuestMeetingToken(token, name.trim());
            setAccess(meetingAccess);
        } catch (err: unknown) {
            setError(refusal(err, t));
            setJoining(false);
        }
    };

    /**
     * The therapist ending the session closes the room, which disconnects the
     * patient. Said plainly, because the alternative is a frozen video and a
     * patient wondering whether their connection dropped mid sentence.
     */
    if (ended) {
        return (
            <MeetingRoom>
                <div className="flex flex-1 items-center justify-center p-6">
                    <div className="w-full max-w-sm rounded-2xl border border-room-line bg-room-raised p-8 text-center">
                        {/*
                          * White, not the brand's own ink: the room is dark
                          * whatever the visitor's theme says, so the logo as
                          * drawn is invisible on it.
                          */}
                        <img
                            src="/images/logo/logo.png"
                            alt="Alchely"
                            width={130}
                            height={32}
                            className="mx-auto mb-6 brightness-0 invert"
                        />
                        <p className="text-sm leading-relaxed text-room-ink">
                            {t("guestMeeting.ended")}
                        </p>
                    </div>
                </div>
            </MeetingRoom>
        );
    }

    if (access) {
        return (
            <MeetingRoom>
                <MeetingHeader title={t("guestMeeting.title")}>
                    <MeetingElapsed room={roomFromToken(token)} running={connected} />
                </MeetingHeader>
                {/* Width-capped like the therapist's, except while a tool's
                    board is up: it wants every pixel. */}
                <div
                    className="mx-auto flex min-h-0 w-full max-w-5xl flex-1 flex-col p-2 sm:p-4 has-[[data-tool-board]]:max-w-none"
                    data-testid="guest-meeting-room"
                >
                    <LiveKitRoom
                        data-lk-theme="default"
                        options={MEETING_ROOM_OPTIONS}
                        serverUrl={access.url}
                        token={access.token}
                        connect={true}
                        /* Whatever the patient chose in the lobby. Arriving on
                           camera before you meant to is not a small thing when
                           the call is a therapy session. */
                        video={devices.camera}
                        audio={devices.microphone}
                        onConnected={() => setConnected(true)}
                        onDisconnected={() => setEnded(true)}
                        className="h-full"
                    >
                        <MeetingStage />
                    </LiveKitRoom>
                </div>
            </MeetingRoom>
        );
    }

    // The lobby. Deliberately a step rather than joining on arrival: opening a
    // link should not put someone on camera before they are ready.
    return (
        <MeetingRoom>
            <MeetingLobby
                name={name}
                onNameChange={setName}
                devices={devices}
                onDevicesChange={setDevices}
                joining={joining}
                error={error}
                onJoin={join}
            />
        </MeetingRoom>
    );
}
